export const CLIENT_BUILD_ID: string = typeof __BUILD_ID__ !== "undefined" ? __BUILD_ID__ : "dev";

type Listener = () => void;
let updateAvailable = false;
const listeners = new Set<Listener>();

export function onUpdateAvailable(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function isUpdateAvailable(): boolean {
  return updateAvailable;
}

/**
 * Zentraler Fetch-Wrapper: vergleicht die Server-Build-ID (x-build-id) mit der
 * eigenen. Weicht sie ab, wurde deployt, während dieser Tab offen war → die UI
 * zeigt einen "Neu laden"-Hinweis (App.tsx).
 */
/** JSON-Convenience über api(): wirft bei !ok mit dem Server-Fehlercode (Anzeige via i18n apiError()). */
export async function apiJson<T>(
  path: string,
  init?: RequestInit & { json?: unknown },
): Promise<T> {
  const { json, ...rest } = init ?? {};
  const res = await api(path, {
    ...rest,
    headers: {
      ...(json !== undefined ? { "content-type": "application/json" } : {}),
      ...rest.headers,
    },
    body: json !== undefined ? JSON.stringify(json) : rest.body,
  });
  if (!res.ok) {
    let message = `http_${res.status}`;
    try {
      const data = (await res.json()) as { error?: string };
      if (data.error) message = data.error;
    } catch {
      /* keine JSON-Antwort */
    }
    throw new Error(message);
  }
  return (await res.json()) as T;
}

/**
 * Datei-Upload mit Fortschritt (XHR, weil fetch keine Upload-Progress-Events hat).
 * Gemeldet werden Bytes, nicht Prozent: die Sammelanzeige rechnet über alle Dateien
 * zusammen — ein Prozentwert je Datei ließe sich dafür nicht addieren.
 */
export function uploadFile(
  url: string,
  file: File,
  onProgress: (loaded: number, total: number) => void,
  signal?: AbortSignal,
): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) return reject(new Error("aborted"));
    const xhr = new XMLHttpRequest();
    signal?.addEventListener("abort", () => xhr.abort(), { once: true });
    xhr.onabort = () => reject(new Error("aborted"));
    xhr.open("PUT", url);
    xhr.setRequestHeader("content-type", file.type);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(e.loaded, e.total);
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) return resolve();
      let message = `http_${xhr.status}`;
      try {
        const data = JSON.parse(xhr.responseText) as { error?: string };
        if (data.error) message = data.error;
      } catch {
        /* ok */
      }
      reject(new Error(message));
    };
    xhr.onerror = () => reject(new Error("network_error"));
    xhr.send(file);
  });
}

export async function api(path: string, init?: RequestInit): Promise<Response> {
  const res = await fetch(path, init);
  const serverBuild = res.headers.get("x-build-id");
  if (
    import.meta.env.PROD && // Vergleich nur zwischen zwei echten Builds —
    serverBuild && // im Dev laufen UI (Vite) und Server (tsx) getrennt
    serverBuild !== "dev" &&
    CLIENT_BUILD_ID !== "dev" &&
    serverBuild !== CLIENT_BUILD_ID &&
    !updateAvailable
  ) {
    updateAvailable = true;
    listeners.forEach((l) => l());
  }
  return res;
}
